/**
 * SuggestionStrip — AI suggestions surfaced above the editor in strip mode.
 *
 * Cards grouped under section headers. Clicking a card focuses it; the
 * focused card becomes the target for ` (speak-tune) and ⌃` — same
 * gesture as inline mode. The ` keydown handler in PrototypeReport reads
 * `focusedStripRowAtom` and routes the voice-adjust pipeline at the card
 * instead of an editor chip. This keeps strip and inline modes
 * gesture-compatible — radiologists don't have to relearn anything.
 *
 * Accept (✓) injects the chip inline + replaces the section's template
 * chip; Dismiss (✕) removes the card without touching the editor.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import type { Editor } from "@tiptap/core";

import {
  findingJumpTarget,
  type CandidateAttribute,
  type FindingJumpTarget,
  type FindingSource,
  type Phrasing,
  type SelectedAttribute,
  type StructuredReportResponse,
} from "../../hooks/useReportQuery";
import type { ReportTemplate } from "../../hooks/useTemplate";
import { focusedStripRowAtom, showLowConfidenceFindingsAtom } from "../../atoms/reporterPrefsAtoms";
import { dictationGhostStripAtom } from "../../atoms/reporterChipAtoms";
import {
  aiFindingChipAttrs,
  canRebuildPhrase,
  defaultAttributeSelection,
  rebuildPhraseFromCondition,
  toggleAttrSelection,
} from "../../utils/reportUtils";
import { ChipAttributePills } from "./ChipAttributePills";
import { ChipKbdHints } from "./ChipKbdHints";

interface StripItem extends FindingSource {
  id: string;
  text: string;
  /** Canonical condition slug (e.g. "liver/hepatic_steatosis") — required
   *  by the pill-toggle phrase rebuild. Passing item.text instead would
   *  stack adjectives. Wire shape (MergeField, ToConsiderItem) always
   *  carries a slug. */
  condition: string;
  section: string;
  seriesId: string | null;
  sliceIndex: number | null;
  frameOfReferenceUid: string | null;
  confidence: "confident" | "maybe";
  candidateAttributes: CandidateAttribute[];
  attributes: SelectedAttribute[];
  /** Engine-emitted structured phrase, carried through so an accepted chip keeps the
   *  phrasing path (the inline auto-build path already does). Without it, accept() would
   *  encode phrasing:null and the chip would fall back to the rebuildPhrase renderer this
   *  work retires. Null for pre-rollout findings that carry none. */
  phrasing: Phrasing | null;
}

function buildItems(
  reportData: StructuredReportResponse | null,
  showLowConfidence: boolean,
): StripItem[] {
  if (!reportData) return [];
  const out: StripItem[] = [];
  for (const [sectionLabel, section] of Object.entries(reportData.sections)) {
    for (const f of section.findings) {
      out.push({
        ...f,
        id: f.id,
        text: f.text,
        condition: f.condition,
        section: sectionLabel,
        seriesId: f.seriesId ?? null,
        sliceIndex: f.sliceIndex ?? null,
        frameOfReferenceUid: f.frameOfReferenceUid ?? null,
        confidence: "confident",
        candidateAttributes: f.candidateAttributes ?? [],
        attributes: f.attributes ?? [],
        phrasing: f.phrasing ?? null,
      });
    }
  }
  // Low-confidence ("maybe") cards are gated by the gear toggle (default off).
  if (!showLowConfidence) return out;
  for (const it of reportData.toConsider) {
    out.push({
      ...it,
      id: it.id,
      text: it.text,
      condition: it.condition,
      section: it.section,
      seriesId: it.seriesId,
      sliceIndex: it.sliceIndex,
      frameOfReferenceUid: it.frameOfReferenceUid ?? null,
      confidence: "maybe",
      candidateAttributes: it.candidateAttributes ?? [],
      attributes: it.attributes ?? [],
      phrasing: it.phrasing ?? null,
    });
  }
  return out;
}

interface SuggestionStripProps {
  editor: Editor | null;
  studyId: string;
  reportData: StructuredReportResponse | null;
  /** Used to resolve a section kind slug back to its display label
   *  (e.g. "liver" -> "LIVER") for the strip's group headers and aria-labels.
   *  The report's `sections` map is keyed by slug; the wire format intentionally
   *  doesn't carry display labels. */
  template: ReportTemplate;
  /** Bumped each time the rad clicks Accept on a strip card. PrototypeReport
   *  reads the parent ref on sign to stamp accepted_findings_count. */
  onAccept?: () => void;
  /** Bumped each time the rad explicitly dismisses (Esc / ✕) a strip card. */
  onDismiss?: () => void;
  /** Called when the rad focuses a card. Triggers the cross-viewport
   *  slice jump alongside the existing focusedStripRowAtom write. */
  onJump?: (target: FindingJumpTarget) => void;
}

export function SuggestionStrip({
  editor,
  studyId,
  reportData,
  template,
  onAccept,
  onDismiss,
  onJump,
}: SuggestionStripProps) {
  const labelByKind = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of template.findings) m.set(s.kind, s.label);
    return m;
  }, [template]);
  const labelFor = useCallback((kind: string) => labelByKind.get(kind) ?? kind, [labelByKind]);
  const showLowConfidence = useAtomValue(showLowConfidenceFindingsAtom);
  const initialItems = useMemo(
    () => buildItems(reportData, showLowConfidence),
    [reportData, showLowConfidence],
  );
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  // Per-row cortex attribute selection. null = use default from candidates.
  const [attrSelections, setAttrSelections] = useState<
    Record<string, Record<string, string | null>>
  >({});
  const [focused, setFocused] = useAtom(focusedStripRowAtom);

  // Reset on study switch. Keyed to studyId, not reportData, so a background
  // refetch (or any parent re-render that mints a new reportData object)
  // doesn't wipe rad accept/dismiss decisions on the current study.
  useEffect(() => {
    setResolvedIds(new Set());
    setAcceptedCount(0);
    setOverrides({});
    setAttrSelections({});
    setFocused(null);
  }, [studyId, setFocused]);

  const setOverrideText = useCallback(
    (rowId: string, next: string) => setOverrides((prev) => ({ ...prev, [rowId]: next })),
    [],
  );

  // Keep the focused atom's `apply` and `originalText` in lockstep with the
  // current override; without this, voiceAdjust would tune the stale text.
  useEffect(() => {
    if (!focused) return;
    const current = overrides[focused.id];
    if (current !== undefined && current !== focused.originalText) {
      setFocused({
        id: focused.id,
        originalText: current,
        apply: (next: string) => setOverrideText(focused.id, next),
      });
    }
  }, [focused, overrides, setFocused, setOverrideText]);

  const pending = useMemo(
    () => initialItems.filter((it) => !resolvedIds.has(it.id)),
    [initialItems, resolvedIds],
  );

  const grouped = useMemo(() => {
    const out: { section: string; items: StripItem[] }[] = [];
    const sectionIndex = new Map<string, number>();
    for (const it of pending) {
      const idx = sectionIndex.get(it.section);
      if (idx === undefined) {
        sectionIndex.set(it.section, out.length);
        out.push({ section: it.section, items: [it] });
      } else {
        out[idx]!.items.push(it);
      }
    }
    return out;
  }, [pending]);

  // Track DOM refs for each card's input so accept/dismiss can re-focus
  // the next pending card after the current one resolves, AND so Tab
  // inside a card can hop to the next/prev card directly. Trapping Tab
  // inside the strip keeps the rad from losing focus into unrelated
  // sidebar elements (series tiles, presets, etc.) mid-triage.
  const rowRefs = useRef(new Map<string, HTMLInputElement>());
  const registerRow = useCallback((id: string, el: HTMLInputElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);

  const focusNextAfter = useCallback(
    (rowId: string) => {
      const ordered = initialItems.map((it) => it.id);
      const idx = ordered.indexOf(rowId);
      if (idx === -1) return;
      // Walk forward, then wrap once, looking for a still-pending row.
      const resolved = new Set(resolvedIds);
      resolved.add(rowId);
      for (let step = 1; step <= ordered.length; step++) {
        const next = ordered[(idx + step) % ordered.length];
        if (!resolved.has(next)) {
          rowRefs.current.get(next)?.focus();
          return;
        }
      }
    },
    [initialItems, resolvedIds],
  );

  const focusCardOffset = useCallback(
    (fromId: string, direction: 1 | -1) => {
      const pendingIds = initialItems.filter((it) => !resolvedIds.has(it.id)).map((it) => it.id);
      const idx = pendingIds.indexOf(fromId);
      if (idx === -1 || pendingIds.length <= 1) return;
      const nextIdx = (idx + direction + pendingIds.length) % pendingIds.length;
      const nextId = pendingIds[nextIdx]!;
      rowRefs.current.get(nextId)?.focus();
    },
    [initialItems, resolvedIds],
  );

  // Every hook above runs unconditionally; the empty-state bail sits here,
  // below the last hook, so a reportData null→populated transition can't
  // change the hook count between renders — otherwise React throws "rendered
  // more hooks than during the previous render" the moment findings load.
  if (initialItems.length === 0) return null;

  const markResolved = (rowId: string) => {
    setResolvedIds((prev) => {
      const next = new Set(prev);
      next.add(rowId);
      return next;
    });
    if (focused?.id === rowId) setFocused(null);
    focusNextAfter(rowId);
  };

  const accept = (item: StripItem) => {
    if (!editor) return;
    const text = overrides[item.id] ?? item.text;
    // Same mapper the inline build (buildReportDoc) uses, so a strip-accepted
    // chip carries the identical attrs (condition slug, sorted candidates, and
    // the engine `phrasing`) as an inline-folded one — the accepted chip uses the
    // phrasing join, not rebuildPhrase. (The pre-accept *preview* toggle below is
    // still on the candidates/rebuildPhrase path; it moves to phrasing once cohorts
    // are re-run — same rollout gate as the rest of WS1.5.)
    editor.commands.acceptIntoSection({
      section: item.section,
      fieldAttrs: aiFindingChipAttrs({ ...item, text }, item.confidence),
    });
    markResolved(item.id);
    setAcceptedCount((n) => n + 1);
    onAccept?.();
  };

  const dismiss = (item: StripItem) => {
    markResolved(item.id);
    onDismiss?.();
  };

  const focusCard = (item: StripItem) => {
    const originalText = overrides[item.id] ?? item.text;
    setFocused({
      id: item.id,
      originalText,
      apply: (next: string) => setOverrideText(item.id, next),
    });
    const jump = findingJumpTarget(item);
    if (jump) onJump?.(jump);
  };

  const togglePill = (item: StripItem, dim: string, value: string) => {
    const current = attrSelections[item.id] ?? defaultAttributeSelection(item.candidateAttributes);
    const next = toggleAttrSelection(current, dim, value);
    setAttrSelections((prev) => ({ ...prev, [item.id]: next }));
    const newPhrase = rebuildPhraseFromCondition(item.condition, next, item.candidateAttributes);
    setOverrideText(item.id, newPhrase);
  };

  return (
    <div className="suggestion-strip" data-testid="suggestion-strip">
      <div className="suggestion-strip__header">
        <span className="suggestion-strip__title">AI SUGGESTIONS</span>
        <span className="suggestion-strip__counts">
          <span className="suggestion-strip__count suggestion-strip__count--pending">
            {pending.length} pending
          </span>
          <span className="suggestion-strip__count-sep">·</span>
          <span className="suggestion-strip__count suggestion-strip__count--accepted">
            {acceptedCount} accepted
          </span>
        </span>
      </div>

      {pending.length === 0 ? (
        <div className="suggestion-strip__empty">All AI suggestions reviewed</div>
      ) : (
        <div className="suggestion-strip__groups">
          {grouped.map(({ section, items }) => (
            <div key={section} className="suggestion-strip__group">
              <div className="suggestion-strip__group-label">{labelFor(section)}</div>
              {items.map((item) => (
                <SuggestionCard
                  key={item.id}
                  item={item}
                  text={overrides[item.id] ?? item.text}
                  sectionLabel={labelFor(item.section)}
                  isFocused={focused?.id === item.id}
                  attrSelected={
                    attrSelections[item.id] ?? defaultAttributeSelection(item.candidateAttributes)
                  }
                  onTogglePill={(dim, value) => togglePill(item, dim, value)}
                  onChangeText={(next) => setOverrideText(item.id, next)}
                  onFocus={() => focusCard(item)}
                  onAccept={() => accept(item)}
                  onDismiss={() => dismiss(item)}
                  onNavigate={(direction) => focusCardOffset(item.id, direction)}
                  registerRow={(el) => registerRow(item.id, el)}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface SuggestionCardProps {
  item: StripItem;
  text: string;
  sectionLabel: string;
  isFocused: boolean;
  attrSelected: Record<string, string | null>;
  onTogglePill: (dim: string, value: string) => void;
  onChangeText: (next: string) => void;
  onFocus: () => void;
  onAccept: () => void;
  onDismiss: () => void;
  onNavigate: (direction: 1 | -1) => void;
  registerRow: (el: HTMLInputElement | null) => void;
}

function SuggestionCard({
  item,
  text,
  sectionLabel,
  isFocused,
  attrSelected,
  onTogglePill,
  onChangeText,
  onFocus,
  onAccept,
  onDismiss,
  onNavigate,
  registerRow,
}: SuggestionCardProps) {
  // Mirror the InlineChipCard pill-render guard so both AI surfaces
  // refuse to toggle when no slug is available (which would otherwise
  // stack adjectives on item.text via the phrase rebuilder).
  const hasPills = canRebuildPhrase(item.candidateAttributes, item.condition);

  // Live dictation ghost — set by PrototypeReport's setGhostText while
  // the rad holds ` / Shift+` over a focused strip card. Splice the
  // ghost at the captured input caret offset so the rad sees their
  // words appear in place (mirrors inline chip ghost UX). No ghost
  // when rowId doesn't match — voice_adjust on this card shows
  // "Refining..." elsewhere, not a transcript.
  const stripGhost = useAtomValue(dictationGhostStripAtom);
  const ghost = stripGhost?.rowId === item.id ? stripGhost : null;

  // Keyboard on the card's input. Tab is trapped inside the strip so the
  // rad doesn't accidentally jump to the series tiles / preset list while
  // triaging. Backspace falls through to default text editing so the rad
  // can edit the suggestion — Escape dismisses, ⏎ accepts.
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      onNavigate(e.shiftKey ? -1 : 1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onAccept();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onDismiss();
      return;
    }
  };

  return (
    <div
      className={`suggestion-strip__card suggestion-strip__card--${item.confidence} ${isFocused ? "suggestion-strip__card--focused" : ""}`}
      data-testid={`suggestion-strip-row-${item.id}`}
    >
      <div className="suggestion-strip__card-row">
        <input
          ref={registerRow}
          type="text"
          className="suggestion-strip__text-input"
          value={text}
          aria-label={`Suggestion in ${sectionLabel} — Enter to accept, Esc to dismiss, \` to speak-tune`}
          title="Click or Tab to edit"
          onChange={(e) => onChangeText(e.target.value)}
          onFocus={onFocus}
          onKeyDown={onInputKeyDown}
        />
        <div className="suggestion-strip__card-actions">
          <button
            type="button"
            tabIndex={-1}
            aria-label="Dismiss suggestion"
            title="Dismiss (Esc)"
            className="suggestion-strip__action suggestion-strip__action--dismiss"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
          >
            ✕
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Accept suggestion"
            title="Accept (Enter)"
            className="suggestion-strip__accept"
            onClick={(e) => {
              e.stopPropagation();
              onAccept();
            }}
          >
            Accept
          </button>
        </div>
      </div>
      {ghost && <StripCardGhost existing={text} ghost={ghost.text} offset={ghost.offset} />}
      {isFocused && (
        <div className="suggestion-strip__card-drawer">
          {hasPills && (
            <ChipAttributePills
              candidates={item.candidateAttributes}
              selected={attrSelected}
              onTogglePill={onTogglePill}
            />
          )}
          <ChipKbdHints showSpeak showAccept showDismiss showNext />
        </div>
      )}
    </div>
  );
}

interface StripCardGhostProps {
  existing: string;
  ghost: string;
  offset: number | null;
}

/**
 * Renders the in-progress dictation transcript spliced into the card's
 * existing text at the caret offset captured at gesture-start. Matches
 * inline chip ghost visual contract: the existing text frames the
 * ghost (dim italic) so the rad sees their words appear in place.
 * Null offset → append to end-of-text (Tab-focused card, no caret).
 */
function StripCardGhost({ existing, ghost, offset }: StripCardGhostProps) {
  const splitAt = offset != null && offset <= existing.length ? offset : existing.length;
  const before = existing.slice(0, splitAt);
  const after = existing.slice(splitAt);
  return (
    <div className="suggestion-strip__card-ghost">
      <span className="suggestion-strip__card-ghost-existing">{before}</span>
      <span className="dictation-ghost">
        {before && !/\s$/.test(before) ? " " : ""}
        {ghost}
      </span>
      <span className="suggestion-strip__card-ghost-existing">
        {after && !/^\s/.test(after) ? " " : ""}
        {after}
      </span>
    </div>
  );
}
